/**
 * O pedacinho do estado do construtor de que a grade precisa.
 *
 * A grade resolve mestre-detalhe perguntando "quais nós estão dentro deste
 * painel?" — e no módulo antigo ela pergunta isso ao `BuilderScreenBetaService`,
 * uma classe de 3.132 linhas com cinco dependências no construtor e uma chamada
 * `setBuilderService(this)` circular.
 *
 * Ela usa **dois membros**. Dois, de 3.132 linhas. Este token é o contrato desses
 * dois — e é o que permite a grade sair do módulo antigo sem levar junto o
 * serviço inteiro.
 *
 * Quem provê:
 *
 *  - no construtor novo e nas telas publicadas, o `LegacyStateShim`, que deriva a
 *    lista plana a partir do `ScreenDocument`;
 *  - no módulo antigo, o próprio `BuilderScreenBetaService`, que já responde a
 *    esses dois membros.
 *
 * Opcional por injeção: uma grade fora de qualquer contexto de tela — num teste,
 * por exemplo — funciona sem mestre-detalhe em vez de estourar.
 */
import { InjectionToken } from '@angular/core';

/** Um nó na lista plana do construtor antigo. */
export interface LegacyFlatNode {
  readonly id: string;
  readonly type?: string;
  readonly parentId?: string | null;
  readonly props?: Record<string, unknown>;
  readonly [key: string]: unknown;
}

/** O contrato mínimo — exatamente o que a grade chama. */
export interface LegacyScreenState {
  /** A lista plana de nós da tela. */
  readonly currentState: { nodes: readonly LegacyFlatNode[] };

  /**
   * Os ids dos nós que estão dentro de um painel, em qualquer profundidade.
   *
   * `Set`, e não lista: é o que o serviço real devolve, e a grade consulta com
   * `has` dentro de um laço sobre todos os nós — em lista, isso seria uma busca
   * linear por nó.
   */
  getNodeIdsUnderPanel(
    nodes: readonly LegacyFlatNode[],
    panelId: string
  ): Set<string>;
}

export const LEGACY_SCREEN_STATE = new InjectionToken<LegacyScreenState>('LEGACY_SCREEN_STATE');
